import { useCallback, useMemo, useState } from 'react';

/**
 * Multi-select with shift-range and toggle support.
 * `items` is the flattened ordered list currently rendered.
 * Items are identified by `.logicalPath`.
 */
export default function useSelection(items) {
  const [selectedPaths, setSelectedPaths] = useState(() => new Set());
  const [anchor, setAnchor] = useState(null);

  const itemIndexByPath = useMemo(() => {
    const map = new Map();
    items.forEach((it, idx) => map.set(it.logicalPath, idx));
    return map;
  }, [items]);

  const isSelected = useCallback((path) => selectedPaths.has(path), [selectedPaths]);

  const clearSelection = useCallback(() => {
    setSelectedPaths(new Set());
    setAnchor(null);
  }, []);

  const selectOnly = useCallback((path) => {
    setSelectedPaths(new Set(path ? [path] : []));
    setAnchor(path || null);
  }, []);

  const toggleSelection = useCallback((path) => {
    setSelectedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
    setAnchor(path);
  }, []);

  const selectRangeTo = useCallback((path) => {
    if (!anchor) {
      selectOnly(path);
      return;
    }
    const a = itemIndexByPath.get(anchor);
    const b = itemIndexByPath.get(path);
    if (a == null || b == null) {
      selectOnly(path);
      return;
    }
    const [from, to] = a <= b ? [a, b] : [b, a];
    const next = new Set();
    for (let i = from; i <= to; i += 1) next.add(items[i].logicalPath);
    setSelectedPaths(next);
  }, [anchor, itemIndexByPath, items, selectOnly]);

  const handleClick = useCallback((path, event) => {
    if (event?.shiftKey) {
      selectRangeTo(path);
    } else if (event?.metaKey || event?.ctrlKey) {
      toggleSelection(path);
    } else {
      selectOnly(path);
    }
  }, [selectOnly, selectRangeTo, toggleSelection]);

  const selectAll = useCallback(() => {
    setSelectedPaths(new Set(items.map((i) => i.logicalPath)));
  }, [items]);

  const selectedItems = useMemo(
    () => items.filter((i) => selectedPaths.has(i.logicalPath)),
    [items, selectedPaths],
  );

  return {
    selectedPaths,
    selectedItems,
    isSelected,
    clearSelection,
    selectOnly,
    toggleSelection,
    selectRangeTo,
    selectAll,
    handleClick,
  };
}
